import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, Plus, Edit, Trash2, Eye, EyeOff, Search, Upload, Star, X, ChevronLeft, ChevronRight, Mic, Loader2, Images, Video, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/actors`;

const CATEGORIES = ['AI_GENERATED', 'REAL_HUMAN', 'CELEBRITY'];
const GENDERS = ['MALE', 'FEMALE'];
const AGE_GROUPS = ['TEEN', 'YOUNG_ADULT', 'ADULT', 'SENIOR'];
const ETHNICITIES = ['INDIAN', 'CHINESE', 'JAPANESE', 'KOREAN', 'SOUTH_EAST_ASIAN', 'MIDDLE_EASTERN', 'CAUCASIAN', 'MEXICAN', 'AFRICAN', 'OTHER'];
const STYLE_TYPES = ['realistic', 'cinematic', 'cartoon', 'anime'];

const emptyForm = {
  name: '', category: 'REAL_HUMAN', gender: 'MALE', age_group: 'ADULT',
  ethnicity: 'OTHER', tags: '', style_type: 'realistic', default_prompt: '',
  is_premium: false, is_active: true, credits: 0,
  thumbnail_url: '', preview_video_url: '', voice_sample_url: '',
  voice_id: '', motion_style_id: '',
  expression_set: '', pose_variants: '',
  byteplus_asset_group_id: '', byteplus_asset_id: '', use_byteplus_asset: false,
};

export default function ActorAssets() {
  const [actors, setActors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [thumbFile, setThumbFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkCsv, setBulkCsv] = useState(null);
  const [bulkZip, setBulkZip] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [cloningVoice, setCloningVoice] = useState(null);
  const [elevenVoices, setElevenVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null); // { type: 'gallery'|'video'|'audio', actor }

  // Filters
  const [fCategory, setFCategory] = useState('');
  const [fGender, setFGender] = useState('');
  const [fEthnicity, setFEthnicity] = useState('');
  const [fSearch, setFSearch] = useState('');

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const limit = 24;

  const fetchActors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (fCategory) params.append('category', fCategory);
      if (fGender) params.append('gender', fGender);
      if (fEthnicity) params.append('ethnicity', fEthnicity);
      if (fSearch) params.append('search', fSearch);
      const res = await axios.get(`${API}?${params}`, { headers });
      setActors(res.data.actors || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load actors'); }
    finally { setLoading(false); }
  }, [page, fCategory, fGender, fEthnicity, fSearch]);

  useEffect(() => { fetchActors(); }, [fetchActors]);
  useEffect(() => { setPage(1); }, [fCategory, fGender, fEthnicity, fSearch]);

  const VOICES_API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/elevenlabs/voices`;
  const fetchElevenVoices = useCallback(async () => {
    if (elevenVoices.length > 0 || voicesLoading) return;
    setVoicesLoading(true); setVoicesError(false);
    try {
      const res = await axios.get(VOICES_API, { headers });
      setElevenVoices(res.data.voices || []);
    } catch {
      setVoicesError(true);
    } finally { setVoicesLoading(false); }
  }, [elevenVoices.length, voicesLoading]);

  useEffect(() => { if (showDialog) fetchElevenVoices(); }, [showDialog, fetchElevenVoices]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm);
    setThumbFile(null); setVideoFile(null); setVoiceFile(null); setGalleryFiles([]);
    setShowDialog(true);
  };

  const openEdit = (actor) => {
    setEditing(actor);
    setForm({
      name: actor.name || '', category: actor.category || 'REAL_HUMAN',
      gender: actor.gender || 'MALE', age_group: actor.age_group || 'ADULT',
      ethnicity: actor.ethnicity || 'OTHER', style_type: actor.style_type || 'realistic',
      tags: (actor.tags || []).join(', '), default_prompt: actor.default_prompt || '',
      is_premium: actor.is_premium || false, is_active: actor.is_active !== false,
      credits: actor.credits || 0,
      thumbnail_url: actor.thumbnail_url || '', preview_video_url: actor.preview_video_url || '',
      voice_sample_url: actor.voice_sample_url || '',
      voice_id: actor.voice_id || '', motion_style_id: actor.motion_style_id || '',
      expression_set: (actor.expression_set || []).join(', '),
      pose_variants: (actor.pose_variants || []).join(', '),
      byteplus_asset_group_id: actor.byteplus_asset_group_id || '',
      byteplus_asset_id: actor.byteplus_asset_id || '',
      use_byteplus_asset: actor.use_byteplus_asset || false,
    });
    setThumbFile(null); setVideoFile(null); setVoiceFile(null); setGalleryFiles([]);
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('category', form.category);
      fd.append('gender', form.gender);
      fd.append('age_group', form.age_group);
      fd.append('ethnicity', form.ethnicity);
      fd.append('style_type', form.style_type);
      fd.append('is_premium', form.is_premium);
      fd.append('is_active', form.is_active);
      fd.append('credits', form.credits);
      if (form.tags) fd.append('tags', JSON.stringify(form.tags.split(',').map(t => t.trim()).filter(Boolean)));
      if (form.default_prompt) fd.append('default_prompt', form.default_prompt);
      if (form.thumbnail_url) fd.append('thumbnail_url', form.thumbnail_url);
      if (form.preview_video_url) fd.append('preview_video_url', form.preview_video_url);
      if (form.voice_sample_url) fd.append('voice_sample_url', form.voice_sample_url);
      fd.append('byteplus_asset_group_id', form.byteplus_asset_group_id || '');
      fd.append('byteplus_asset_id', form.byteplus_asset_id || '');
      fd.append('use_byteplus_asset', form.use_byteplus_asset);
      // Always send voice_id (empty string clears the selection on edit).
      // Skip only on create when empty so sample auto-cloning still triggers.
      if (editing || form.voice_id) fd.append('voice_id', form.voice_id || '');
      if (form.motion_style_id) fd.append('motion_style_id', form.motion_style_id);
      if (form.expression_set) fd.append('expression_set', JSON.stringify(form.expression_set.split(',').map(t => t.trim()).filter(Boolean)));
      if (form.pose_variants) fd.append('pose_variants', JSON.stringify(form.pose_variants.split(',').map(t => t.trim()).filter(Boolean)));
      if (thumbFile) fd.append('thumbnail_file', thumbFile);
      if (videoFile) fd.append('preview_video_file', videoFile);
      if (voiceFile) fd.append('voice_sample_file', voiceFile);
      galleryFiles.forEach(f => fd.append('gallery_files', f));

      if (editing) {
        await axios.put(`${API}/${editing.id}`, fd, { headers });
        toast.success('Actor updated');
      } else {
        await axios.post(API, fd, { headers });
        toast.success('Actor created');
      }
      setShowDialog(false); fetchActors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this actor permanently?')) return;
    try { await axios.delete(`${API}/${id}`, { headers }); toast.success('Actor deleted'); fetchActors(); }
    catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (actor) => {
    try {
      const fd = new FormData();
      fd.append('is_active', !actor.is_active);
      await axios.put(`${API}/${actor.id}`, fd, { headers });
      toast.success(actor.is_active ? 'Actor hidden' : 'Actor visible');
      fetchActors();
    } catch { toast.error('Toggle failed'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkCsv) { toast.error('CSV file is required'); return; }
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('csv_file', bulkCsv);
      if (bulkZip) fd.append('zip_file', bulkZip);
      const res = await axios.post(`${API}/bulk-upload`, fd, { headers });
      toast.success(`Created ${res.data.created_count} actors`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} rows had errors`);
      setShowBulk(false); setBulkCsv(null); setBulkZip(null); fetchActors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally { setBulkUploading(false); }
  };

  const handleCloneVoice = async (actor) => {
    if (!actor.voice_sample_url) { toast.error('No voice sample uploaded'); return; }
    setCloningVoice(actor.id);
    try {
      const res = await axios.post(`${API}/${actor.id}/clone-voice`, {}, { headers });
      toast.success(`Voice cloned! ID: ${res.data.voice_id}`);
      fetchActors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Voice cloning failed');
    } finally { setCloningVoice(null); }
  };

  const totalPages = Math.ceil(total / limit);

  const categoryColor = (c) => ({ AI_GENERATED: 'bg-violet-100 text-violet-700', REAL_HUMAN: 'bg-sky-100 text-sky-700', CELEBRITY: 'bg-amber-100 text-amber-700' }[c] || 'bg-slate-100 text-slate-700');

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 data-testid="actors-page-title" className="text-4xl font-bold text-slate-800 mb-1">Actor Assets Library</h1>
          <p className="text-slate-500 text-sm">{total} actors in library</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="bulk-upload-btn" variant="outline" onClick={() => setShowBulk(true)}>
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button data-testid="add-actor-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Add Actor
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-end" data-testid="actors-filters">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-500">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input data-testid="actors-search" className="pl-8" placeholder="Search by name or tags..." value={fSearch} onChange={e => setFSearch(e.target.value)} />
          </div>
        </div>
        <div className="w-[150px]">
          <Label className="text-xs text-slate-500">Category</Label>
          <Select value={fCategory} onValueChange={v => setFCategory(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[130px]">
          <Label className="text-xs text-slate-500">Gender</Label>
          <Select value={fGender} onValueChange={v => setFGender(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-gender"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[160px]">
          <Label className="text-xs text-slate-500">Ethnicity</Label>
          <Select value={fEthnicity} onValueChange={v => setFEthnicity(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-ethnicity"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {ETHNICITIES.map(e => <SelectItem key={e} value={e}>{e.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(fCategory || fGender || fEthnicity || fSearch) && (
          <Button variant="ghost" size="sm" onClick={() => { setFCategory(''); setFGender(''); setFEthnicity(''); setFSearch(''); }}>
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading actors...</div>
      ) : actors.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg shadow-sm">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-4">No actors found</p>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add Actor</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="actors-grid">
            {actors.map(actor => (
              <div key={actor.id} data-testid={`actor-card-${actor.id}`}
                className={`bg-white rounded-lg shadow-sm overflow-hidden border border-slate-100 hover:shadow-md transition-shadow group ${!actor.is_active ? 'opacity-50' : ''}`}>
                {/* Thumbnail */}
                <div className="aspect-[3/4] bg-slate-100 relative overflow-hidden">
                  {actor.thumbnail_url ? (
                    <img src={actor.thumbnail_url} alt={actor.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Users className="w-10 h-10 text-slate-300" /></div>
                  )}
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button onClick={() => openEdit(actor)} data-testid={`actor-edit-${actor.id}`} className="p-2 rounded-full bg-white/90 text-blue-600 hover:bg-white"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => toggleActive(actor)} data-testid={`actor-toggle-${actor.id}`} className="p-2 rounded-full bg-white/90 text-slate-600 hover:bg-white">
                      {actor.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleDelete(actor.id)} data-testid={`actor-delete-${actor.id}`} className="p-2 rounded-full bg-white/90 text-red-600 hover:bg-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex gap-1">
                    {actor.is_premium && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-400 text-white flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />PRO</span>}
                  </div>
                  {actor.credits > 0 && (
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500 text-white">{actor.credits} cr</span>
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-2.5">
                  <div className="font-medium text-sm text-slate-800 truncate">{actor.name}</div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${categoryColor(actor.category)}`}>{actor.category?.replace('_', ' ')}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">{actor.gender}</span>
                  </div>
                  {actor.tags?.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {actor.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] bg-slate-50 text-slate-500 border border-slate-200">{tag}</span>
                      ))}
                      {actor.tags.length > 3 && <span className="text-[9px] text-slate-400">+{actor.tags.length - 3}</span>}
                    </div>
                  )}
                  {/* Voice status */}
                  {actor.voice_id ? (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Mic className="w-3 h-3 text-emerald-500" />
                      <span className="text-[9px] text-emerald-600 font-medium truncate" title={actor.voice_id}>Voice Cloned</span>
                    </div>
                  ) : actor.voice_sample_url ? (
                    <button
                      data-testid={`actor-clone-voice-${actor.id}`}
                      onClick={(e) => { e.stopPropagation(); handleCloneVoice(actor); }}
                      disabled={cloningVoice === actor.id}
                      className="flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                    >
                      {cloningVoice === actor.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
                      {cloningVoice === actor.id ? 'Cloning...' : 'Clone Voice'}
                    </button>
                  ) : null}
                  {/* Media preview icons */}
                  {(actor.image_gallery?.length > 0 || actor.preview_video_url || actor.voice_sample_url) && (
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100">
                      {actor.image_gallery?.length > 0 && (
                        <button data-testid={`actor-preview-gallery-${actor.id}`} onClick={(e) => { e.stopPropagation(); setPreviewMedia({ type: 'gallery', actor }); }}
                          className="p-1.5 rounded-md bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors" title="View Gallery">
                          <Images className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {actor.preview_video_url && (
                        <button data-testid={`actor-preview-video-${actor.id}`} onClick={(e) => { e.stopPropagation(); setPreviewMedia({ type: 'video', actor }); }}
                          className="p-1.5 rounded-md bg-slate-50 text-slate-500 hover:bg-purple-50 hover:text-purple-600 transition-colors" title="Preview Video">
                          <Video className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {actor.voice_sample_url && (
                        <button data-testid={`actor-preview-audio-${actor.id}`} onClick={(e) => { e.stopPropagation(); setPreviewMedia({ type: 'audio', actor }); }}
                          className="p-1.5 rounded-md bg-slate-50 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" title="Listen Voice Sample">
                          <Volume2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6" data-testid="actors-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Actor' : 'Add New Actor'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Name */}
              <div>
                <Label>Name *</Label>
                <Input data-testid="actor-form-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              {/* Row: Category, Gender, Age, Ethnicity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="actor-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_',' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                    <SelectTrigger data-testid="actor-form-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Age Group</Label>
                  <Select value={form.age_group} onValueChange={v => setForm({...form, age_group: v})}>
                    <SelectTrigger data-testid="actor-form-age"><SelectValue /></SelectTrigger>
                    <SelectContent>{AGE_GROUPS.map(a => <SelectItem key={a} value={a}>{a.replace('_',' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ethnicity</Label>
                  <Select value={form.ethnicity} onValueChange={v => setForm({...form, ethnicity: v})}>
                    <SelectTrigger data-testid="actor-form-ethnicity"><SelectValue /></SelectTrigger>
                    <SelectContent>{ETHNICITIES.map(e => <SelectItem key={e} value={e}>{e.replace('_',' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {/* Style + Credits */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Style Type</Label>
                  <Select value={form.style_type} onValueChange={v => setForm({...form, style_type: v})}>
                    <SelectTrigger data-testid="actor-form-style"><SelectValue /></SelectTrigger>
                    <SelectContent>{STYLE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Credits (deducted on selection)</Label>
                  <Input data-testid="actor-form-credits" type="number" min="0" value={form.credits} onChange={e => setForm({...form, credits: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              {/* Tags */}
              <div>
                <Label>Tags (comma separated)</Label>
                <Input data-testid="actor-form-tags" placeholder="corporate, fitness, luxury" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} />
              </div>
              {/* Media: Thumbnail */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Thumbnail</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">URL</Label>
                    <Input data-testid="actor-form-thumb-url" placeholder="https://..." value={form.thumbnail_url} onChange={e => setForm({...form, thumbnail_url: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">or Upload</Label>
                    <Input data-testid="actor-form-thumb-file" type="file" accept="image/*" onChange={e => setThumbFile(e.target.files[0])} />
                  </div>
                </div>
              </div>
              {/* Media: Preview Video */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Preview Video (optional)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">URL</Label>
                    <Input data-testid="actor-form-video-url" placeholder="https://..." value={form.preview_video_url} onChange={e => setForm({...form, preview_video_url: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">or Upload</Label>
                    <Input data-testid="actor-form-video-file" type="file" accept="video/*" onChange={e => setVideoFile(e.target.files[0])} />
                  </div>
                </div>
              </div>
              {/* BytePlus ModelArk — Seedance */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50" data-testid="actor-form-byteplus-section">
                <Label className="text-sm font-semibold">BytePlus ModelArk — Seedance (optional)</Label>
                <p className="text-xs text-slate-500">Use an already-approved actor asset from your BytePlus ModelArk account for Seedance video generation.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Asset Group ID</Label>
                    <Input data-testid="actor-form-byteplus-group-id" placeholder="Enter BytePlus ModelArk Group ID" value={form.byteplus_asset_group_id} onChange={e => setForm({...form, byteplus_asset_group_id: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Asset ID</Label>
                    <Input data-testid="actor-form-byteplus-asset-id" placeholder="Enter BytePlus ModelArk Asset ID" value={form.byteplus_asset_id} onChange={e => setForm({...form, byteplus_asset_id: e.target.value})} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" data-testid="actor-form-byteplus-toggle" checked={form.use_byteplus_asset} onChange={e => setForm({...form, use_byteplus_asset: e.target.checked})} className="w-4 h-4 accent-purple-600" />
                  Use BytePlus asset for Seedance generation
                </label>
              </div>
              {/* Voice (ElevenLabs) */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50" data-testid="actor-form-voice-section">
                <Label className="text-sm font-semibold flex items-center gap-2"><Volume2 className="w-4 h-4" /> Voice (ElevenLabs)</Label>

                {/* Option A: pick an existing voice from the connected account */}
                <div>
                  <Label className="text-xs text-slate-500">Select a voice from your ElevenLabs account</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1">
                      <Select
                        value={form.voice_id || 'none'}
                        onValueChange={v => setForm({ ...form, voice_id: v === 'none' ? '' : v })}
                        disabled={voicesLoading}
                      >
                        <SelectTrigger data-testid="actor-form-voice-select">
                          <SelectValue placeholder={voicesLoading ? 'Loading voices…' : 'No voice selected'} />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="none">No voice selected</SelectItem>
                          {elevenVoices.map(v => (
                            <SelectItem key={v.voice_id} value={v.voice_id}>
                              {(v.name && v.name.trim()) || 'Unnamed voice'}{v.category && v.category !== 'premade' ? ` · ${v.category}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {form.voice_id && (
                      <Button type="button" variant="outline" size="sm" data-testid="actor-form-voice-clear"
                        onClick={() => setForm({ ...form, voice_id: '' })}>Clear</Button>
                    )}
                  </div>
                  {voicesError && (
                    <p className="text-xs text-amber-600 mt-1">Couldn't load ElevenLabs voices — check the ElevenLabs key in AI Providers.</p>
                  )}
                  {form.voice_id && (() => {
                    const sel = elevenVoices.find(v => v.voice_id === form.voice_id);
                    return (
                      <div className="flex items-center gap-2 mt-2" data-testid="actor-form-voice-selected">
                        <span className="text-xs text-emerald-700 font-medium">
                          Selected: {sel ? sel.name : form.voice_id}
                        </span>
                        {sel?.preview_url && <audio controls src={sel.preview_url} className="h-7" />}
                      </div>
                    );
                  })()}
                </div>

                <div className="text-[11px] text-slate-400 text-center">— or clone a new voice from a sample —</div>

                {/* Option B: upload a sample to clone (auto-clones on save when no voice is selected) */}
                <div>
                  <Label className="text-xs text-slate-500">Voice sample for cloning</Label>
                  {editing?.voice_sample_url && (
                    <div className="flex items-center gap-2 mt-1">
                      <audio controls src={editing.voice_sample_url} className="h-8" />
                      <span className="text-xs text-slate-400 truncate max-w-[200px]">{editing.voice_sample_url}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <div>
                      <Label className="text-xs text-slate-500">URL</Label>
                      <Input data-testid="actor-form-voice-sample-url" placeholder="https://..." value={form.voice_sample_url || ''} onChange={e => setForm({...form, voice_sample_url: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">or Upload</Label>
                      <Input data-testid="actor-form-voice-sample-file" type="file" accept="audio/*" onChange={e => setVoiceFile(e.target.files[0])} />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {form.voice_id
                      ? 'A voice is selected above — the sample will be saved but not cloned. Clear the selection to clone from the sample instead.'
                      : 'On save, this sample is cloned into your ElevenLabs account and linked to the actor.'}
                  </p>
                </div>
              </div>
              {/* Gallery */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Image Gallery</Label>
                {editing?.image_gallery?.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {editing.image_gallery.map((url, i) => (
                      <div key={i} className="relative w-16 h-16 rounded overflow-hidden border">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
                <Input data-testid="actor-form-gallery" type="file" accept="image/*" multiple onChange={e => setGalleryFiles(Array.from(e.target.files))} />
                <p className="text-xs text-slate-400">Select multiple images to add to the gallery</p>
              </div>
              {/* Default Prompt */}
              <div>
                <Label>Default Prompt (for AI consistency)</Label>
                <Textarea data-testid="actor-form-prompt" placeholder="A professional-looking Indian man in his 30s..." value={form.default_prompt} onChange={e => setForm({...form, default_prompt: e.target.value})} rows={2} />
              </div>
              {/* Expressions + Poses */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Expressions (comma separated)</Label>
                  <Input data-testid="actor-form-expressions" placeholder="happy, serious, angry" value={form.expression_set} onChange={e => setForm({...form, expression_set: e.target.value})} />
                </div>
                <div>
                  <Label>Pose Variants (comma separated)</Label>
                  <Input data-testid="actor-form-poses" placeholder="standing, sitting, walking" value={form.pose_variants} onChange={e => setForm({...form, pose_variants: e.target.value})} />
                </div>
              </div>
              {/* Voice + Motion IDs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Voice ID {form.voice_id ? <span className="text-emerald-500 text-xs ml-1">(cloned)</span> : <span className="text-slate-400 text-xs ml-1">(auto-set on clone)</span>}</Label>
                  <div className="flex gap-2">
                    <Input data-testid="actor-form-voice" placeholder="auto-populated on voice clone" value={form.voice_id} onChange={e => setForm({...form, voice_id: e.target.value})} className="flex-1" />
                    {editing && editing.voice_sample_url && (
                      <Button
                        type="button"
                        data-testid="actor-form-clone-btn"
                        variant="outline"
                        size="sm"
                        disabled={cloningVoice === editing.id}
                        onClick={() => handleCloneVoice(editing)}
                        className="shrink-0 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      >
                        {cloningVoice === editing.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mic className="w-4 h-4 mr-1" />}
                        {cloningVoice === editing.id ? 'Cloning...' : 'Clone'}
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Motion Style ID (optional)</Label>
                  <Input data-testid="actor-form-motion" placeholder="motion_style_id" value={form.motion_style_id} onChange={e => setForm({...form, motion_style_id: e.target.value})} />
                </div>
              </div>
              {/* Toggles */}
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch data-testid="actor-form-premium" checked={form.is_premium} onCheckedChange={v => setForm({...form, is_premium: v})} />
                  <Label>Premium Actor</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="actor-form-active" checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="actor-form-submit" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Saving...' : editing ? 'Update Actor' : 'Create Actor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Bulk Upload Actors</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>CSV File (required) *</Label>
              <Input data-testid="bulk-csv" type="file" accept=".csv" onChange={e => setBulkCsv(e.target.files[0])} />
              <p className="text-xs text-slate-400 mt-1">Columns: name, category, gender, age_group, ethnicity, tags, style_type, is_premium, credits, default_prompt</p>
            </div>
            <div>
              <Label>ZIP File (optional, images)</Label>
              <Input data-testid="bulk-zip" type="file" accept=".zip" onChange={e => setBulkZip(e.target.files[0])} />
              <p className="text-xs text-slate-400 mt-1">Images named: actor_name_thumbnail.jpg, actor_name_1.jpg, etc.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button data-testid="bulk-upload-submit" onClick={handleBulkUpload} disabled={bulkUploading} className="bg-blue-600 hover:bg-blue-700">
              {bulkUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="media-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {previewMedia?.type === 'gallery' && `${previewMedia.actor.name} — Image Gallery`}
              {previewMedia?.type === 'video' && `${previewMedia.actor.name} — Preview Video`}
              {previewMedia?.type === 'audio' && `${previewMedia.actor.name} — Voice Sample`}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {previewMedia?.type === 'gallery' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="media-gallery-grid">
                {previewMedia.actor.image_gallery?.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="block aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors">
                    <img src={url} alt={`${previewMedia.actor.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            {previewMedia?.type === 'video' && (
              <div data-testid="media-video-player">
                <video controls autoPlay className="w-full rounded-lg" src={previewMedia.actor.preview_video_url}>
                  Your browser does not support video playback.
                </video>
              </div>
            )}
            {previewMedia?.type === 'audio' && (
              <div className="flex flex-col items-center gap-4 py-6" data-testid="media-audio-player">
                <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Volume2 className="w-10 h-10 text-emerald-500" />
                </div>
                <p className="text-sm text-slate-500">Voice sample for <span className="font-medium text-slate-700">{previewMedia.actor.name}</span></p>
                <audio controls autoPlay className="w-full max-w-md" src={previewMedia.actor.voice_sample_url}>
                  Your browser does not support audio playback.
                </audio>
                {previewMedia.actor.voice_id && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1"><Mic className="w-3 h-3" /> Cloned Voice ID: {previewMedia.actor.voice_id}</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
